import { Badge } from "@workspace/web-ui/components/badge";
import { Separator } from "@workspace/web-ui/components/separator";
import { MedicalPrescription } from "~/features/dashboard/pages/medicals/types";
import { formatMedicationDate } from "@workspace/lib/date";
import { Paginator } from "./pagination";
import { useState } from "react";

interface Medication {
  medicalItems: MedicalPrescription[];
  program?: string;
  isLoading?: boolean;
  totalCount?: number;
}

const ITEMS_PER_PAGE = 5; // Display 5 items per page


export const getStatusConfig = (item: MedicalPrescription) => {
  let status: string

  if (item.is_due) {
    status = "due"
  } else {
    status = item.status
  }

  const statusMap = {
    "active": { color: "bg-[#E6FAF2] text-[#00C781] hover:bg-[#E6FAF2]", text: "Active", status: "active" },
    "new": { color: "bg-[#FEF2FF] text-[#942BA9]", text: "New", status: "new" },
    "due": { color: "bg-[#FEEBEB] text-[#F53535] hover:bg-[#FEEBEB]", text: "Out of supply", status: "due" },
    "refill_requested": { color: "bg-[#FFF6E7] text-[#FFAB1F]", text: "Refill requested", status: "refill_requested" },
  };

  return statusMap[status as keyof typeof statusMap];
};

export function ActiveMedication({ medicalItems, totalCount }: Medication) {
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate total pages
  const totalPages = Math.ceil(medicalItems.length / ITEMS_PER_PAGE);

  // Get data for the current page
  const paginatedData = medicalItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const renderButtons = (item: MedicalPrescription) => {
    const statusConfig = getStatusConfig(item)

    return (
      <div key={item.medication.medication_id} className="flex flex-col border border-[#0404041A] shadow-sm rounded-[8px] mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full py-3 sm:py-[14px] px-3 sm:px-[20px] gap-3 sm:gap-0">
          <div className="flex flex-col gap-1 w-full">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-medium text-base sm:text-lg text-[#2D2F35]">{item.medication.name}</h3>
              <span className="text-xs sm:text-sm">({item.medication.duration} supply)</span>
              <Badge className={`${statusConfig.color} text-xs font-medium`}><span className=" text-[00C781] mr-1 ">{"\u2B24"}</span> {statusConfig.text}</Badge>
            </div>

            <div className="flex">
              <h6 className="text-xs sm:text-sm font-normal text-[#202020CC] text-left">
                Prescribed: {formatMedicationDate(item.prescribed_on || "")} <span className="mx-1 text-[#C9C9CC]">{"\u2022"}</span>
                {item.medication.dosage} <span className="mx-1 text-[#C9C9CC]">{"\u2022"}</span> {item.medication.duration}
              </h6>
            </div>
          </div>
        </div>
        {/* Instructions */}
        {item.medication.instructions && (
          <div className="flex flex-col gap-1 w-full bg-[#F0F5FF] py-3 sm:py-[12px] px-3 sm:px-[20px] transition-colors border-[#4272DD] border-l-4 rounded-b-[8px]">
            <h6 className="text-xs sm:text-sm font-medium text-[#4272DD] text-left">
              Instructions
            </h6>
            <p className="text-xs sm:text-sm font-normal text-[#202020CC] text-left">
              {item.medication.instructions}
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="border rounded-xl shadow-sm bg-white w-full text-center mb-4 ">
      <h3 className="flex items-center text-base sm:text-lg font-medium text-left p-4 sm:p-5 text-[#1A202C]">Active medications <Badge className="ml-2 size-4 rounded-full bg-[#4272DD] border border-[#0404041A] p-2 justify-center items-center text-center text-white text-xs">
        {totalCount || 0}
      </Badge></h3>
      <Separator />
      <div className="space-y-4 bg-card rounded-[8px] p-3 sm:p-4">
        {paginatedData.map((med) => renderButtons(med))}
      </div>

      <Paginator items={medicalItems} totalPages={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} itemsPerPage={ITEMS_PER_PAGE} className="border-t" />
    </div>
  )
}
